'use client';

import { useCallback, useMemo, useState } from 'react';
import {
  Calendar01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
} from '@terra/ui-icons';
import { parseDate, type DateValue } from '@internationalized/date';
import { DatePicker } from '../../components/date-picker';
import { Input } from '../../components/input';
import { IconButton } from '../../components/icon-button';
import { Button } from '../../components/button';
import { Box } from '@terra/ui-styled-system';
import { useDateFormatter } from '@terra/ui-formatters';

type CalendarDay = {
  year: number;
  month: number;
  day: number;
};

function getLocalCalendarDate({ year, month, day }: CalendarDay): Date {
  return new Date(year, month - 1, day);
}

function getUtcCalendarDate({ year, month, day }: CalendarDay): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

type SingleDatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  width?: string;
  disableFutureDates?: boolean;
};

export function SingleDatePicker({
  value,
  onChange,
  placeholder,
  size = 'sm',
  disabled = false,
  width,
  disableFutureDates = false,
}: SingleDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dateFormatter = useDateFormatter('en-US');
  const isoDateValue = useMemo(() => {
    if (!value) return '';

    return new Date(value).toISOString().split('T')[0];
  }, [value]);
  const formatDate = useCallback(
    (date: DateValue) => {
      return dateFormatter.formatDateOnly(getLocalCalendarDate(date));
    },
    [dateFormatter],
  );
  const dateValue = useMemo((): DateValue[] | undefined => {
    if (!isoDateValue) return undefined;

    try {
      return [parseDate(isoDateValue)];
    } catch {
      return undefined;
    }
  }, [isoDateValue]);
  const today = useMemo(() => {
    const currentDate = new Date();

    currentDate.setHours(0, 0, 0, 0);

    return currentDate;
  }, []);
  const isFutureCalendarDayDisabled = useCallback(
    (calendarDay: CalendarDay) => {
      if (!disableFutureDates) {
        return false;
      }

      return getLocalCalendarDate(calendarDay).getTime() > today.getTime();
    },
    [disableFutureDates, today],
  );
  const handleValueChange = useCallback(
    (details: { value: DateValue[]; valueAsString: string[] }) => {
      const [selectedDate] = details.value;

      if (selectedDate) {
        if (isFutureCalendarDayDisabled(selectedDate)) {
          return;
        }

        const selectedDateValue = getUtcCalendarDate(selectedDate);

        onChange(selectedDateValue.toISOString());
      } else {
        onChange('');
      }

      setIsOpen(false);
    },
    [isFutureCalendarDayDisabled, onChange],
  );

  return (
    <DatePicker.Root
      startOfWeek={1}
      selectionMode="single"
      value={dateValue}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      disabled={disabled}
      format={formatDate}
      width={width}
    >
      <DatePicker.Control w="full">
        <DatePicker.Trigger asChild>
          <Box position="relative" w="full">
            <DatePicker.Input asChild>
              <Input size={size} placeholder={placeholder} pr="10" />
            </DatePicker.Input>
            <IconButton
              variant="ghost"
              size={size === 'sm' ? 'xs' : 'sm'}
              position="absolute"
              top="50%"
              right="1"
              transform="translateY(-50%)"
              zIndex={1}
            >
              <Calendar01Icon size={size === 'sm' ? 12 : 16} />
            </IconButton>
          </Box>
        </DatePicker.Trigger>
      </DatePicker.Control>
      <DatePicker.Positioner>
        <DatePicker.Content>
          <DatePicker.View view="day">
            <DatePicker.Context>
              {(api) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowLeft01Icon size={16} />
                      </IconButton>
                    </DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <DatePicker.RangeText />
                      </Button>
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowRight01Icon size={16} />
                      </IconButton>
                    </DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableHead>
                      <DatePicker.TableRow>
                        {api.weekDays.map((weekDay, id) => (
                          <DatePicker.TableHeader key={id}>
                            {weekDay.narrow}
                          </DatePicker.TableHeader>
                        ))}
                      </DatePicker.TableRow>
                    </DatePicker.TableHead>
                    <DatePicker.TableBody>
                      {api.weeks.map((week, id) => (
                        <DatePicker.TableRow key={id}>
                          {week.map((day, id) => {
                            const isDisabled = isFutureCalendarDayDisabled(day);

                            return (
                              <DatePicker.TableCell
                                key={id}
                                value={day}
                                disabled={isDisabled}
                              >
                                <DatePicker.TableCellTrigger asChild>
                                  <IconButton variant="ghost">
                                    {day.day}
                                  </IconButton>
                                </DatePicker.TableCellTrigger>
                              </DatePicker.TableCell>
                            );
                          })}
                        </DatePicker.TableRow>
                      ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
          <DatePicker.View view="month">
            <DatePicker.Context>
              {(api) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowLeft01Icon size={16} />
                      </IconButton>
                    </DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <DatePicker.RangeText />
                      </Button>
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowRight01Icon size={16} />
                      </IconButton>
                    </DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableBody>
                      {api
                        .getMonthsGrid({ columns: 4, format: 'short' })
                        .map((months, id) => (
                          <DatePicker.TableRow key={id}>
                            {months.map((month, id) => (
                              <DatePicker.TableCell
                                key={id}
                                value={month.value}
                              >
                                <DatePicker.TableCellTrigger asChild>
                                  <Button variant="ghost">{month.label}</Button>
                                </DatePicker.TableCellTrigger>
                              </DatePicker.TableCell>
                            ))}
                          </DatePicker.TableRow>
                        ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
          <DatePicker.View view="year">
            <DatePicker.Context>
              {(api) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowLeft01Icon size={16} />
                      </IconButton>
                    </DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <DatePicker.RangeText />
                      </Button>
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <ArrowRight01Icon size={16} />
                      </IconButton>
                    </DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableBody>
                      {api.getYearsGrid({ columns: 4 }).map((years, id) => (
                        <DatePicker.TableRow key={id}>
                          {years.map((year, id) => (
                            <DatePicker.TableCell key={id} value={year.value}>
                              <DatePicker.TableCellTrigger asChild>
                                <Button variant="ghost">{year.label}</Button>
                              </DatePicker.TableCellTrigger>
                            </DatePicker.TableCell>
                          ))}
                        </DatePicker.TableRow>
                      ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
        </DatePicker.Content>
      </DatePicker.Positioner>
    </DatePicker.Root>
  );
}
